import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { UserPlus, RotateCcw } from 'lucide-react';

export default function AdminAssignments() {
  const [cases, setCases] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignModal, setAssignModal] = useState(null);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedRole, setSelectedRole] = useState('lawyer');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [casesRes, usersRes] = await Promise.all([
        api.get('/admin/cases', { params: { page_size: 100 } }),
        api.get('/admin/users')
      ]);
      setCases(casesRes.data.cases);
      setUsers(usersRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedUser || !assignModal) return;
    try {
      await api.post('/admin/assign', {
        case_id: assignModal.id,
        user_id: selectedUser,
        role: selectedRole
      });
      setAssignModal(null);
      setSelectedUser('');
      await loadData();
    } catch (err) {
      console.error('Assign failed:', err);
    }
  };

  const handlePullback = async (caseId) => {
    try {
      await api.post('/admin/pullback', { case_id: caseId, reason: 'Admin pullback' });
      await loadData();
    } catch (err) {
      console.error('Pullback failed:', err);
    }
  };

  const lawyers = users.filter(u => u.role === 'lawyer');
  const agents = users.filter(u => u.role === 'agent');

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="admin-assignments">
      <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">Assignments</h1>
      <p className="text-sm text-gray-500 mb-6">Assign and manage case assignments</p>

      {/* Assignment Buckets */}
      <div className="grid grid-cols-3 border border-gray-200 mb-6">
        <div className="p-4 border-r border-gray-200">
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400 block mb-1">Unassigned</span>
          <span className="font-heading text-2xl font-bold">{cases.filter(c => c.assignment_status === 'unassigned').length}</span>
        </div>
        <div className="p-4 border-r border-gray-200">
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400 block mb-1">Assigned</span>
          <span className="font-heading text-2xl font-bold">{cases.filter(c => c.assignment_status === 'assigned').length}</span>
        </div>
        <div className="p-4">
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400 block mb-1">Completed</span>
          <span className="font-heading text-2xl font-bold">{cases.filter(c => c.assignment_status === 'completed').length}</span>
        </div>
      </div>

      {/* Cases Table */}
      <div className="border border-gray-200">
        <table className="w-full" data-testid="assignments-table">
          <thead>
            <tr className="border-b border-gray-200 bg-surface">
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Case</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Client</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Service</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Assignment</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {cases.map(c => (
              <tr key={c.id} className="border-b border-gray-100">
                <td className="px-4 py-3 font-mono text-sm">{c.case_number}</td>
                <td className="px-4 py-3 text-sm">{c.client_name || '—'}</td>
                <td className="px-4 py-3 text-sm">{c.service_type || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-xs font-mono uppercase rounded-sm ${
                    c.assignment_status === 'assigned' ? 'bg-green-100 text-green-700' :
                    c.assignment_status === 'unassigned' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>{c.assignment_status}</span>
                </td>
                <td className="px-4 py-3 flex gap-2">
                  {c.assignment_status === 'unassigned' && (
                    <button
                      data-testid={`assign-btn-${c.id}`}
                      onClick={() => setAssignModal(c)}
                      className="text-xs px-3 py-1 bg-primary text-white rounded-sm hover:bg-primary-hover transition flex items-center gap-1"
                    >
                      <UserPlus className="w-3 h-3" /> Assign
                    </button>
                  )}
                  {c.assignment_status === 'assigned' && (
                    <button
                      data-testid={`pullback-btn-${c.id}`}
                      onClick={() => handlePullback(c.id)}
                      className="text-xs px-3 py-1 bg-destructive text-white rounded-sm hover:opacity-80 transition flex items-center gap-1"
                    >
                      <RotateCcw className="w-3 h-3" /> Pullback
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Assign Modal */}
      {assignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="assign-modal">
          <div className="bg-white w-full max-w-md border border-gray-200 rounded-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="font-heading text-lg font-semibold">Assign Case {assignModal.case_number}</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-gray-500 block mb-2">Role</label>
                <select
                  data-testid="assign-role-select"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                >
                  <option value="lawyer">Lawyer</option>
                  <option value="agent">Agent</option>
                </select>
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-gray-500 block mb-2">
                  {selectedRole === 'lawyer' ? 'Lawyer' : 'Agent'}
                </label>
                <select
                  data-testid="assign-user-select"
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                >
                  <option value="">Select...</option>
                  {(selectedRole === 'lawyer' ? lawyers : agents).map(u => (
                    <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-2 justify-end">
              <button onClick={() => { setAssignModal(null); setSelectedUser(''); }} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-50">Cancel</button>
              <button
                data-testid="assign-confirm-btn"
                onClick={handleAssign}
                disabled={!selectedUser}
                className="px-4 py-2 text-sm bg-black text-white rounded-sm hover:bg-gray-800 disabled:opacity-30"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
